import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { useI18N, useLanguage } from "@src/language/language-context";

interface TimezoneDateProps {
  date: string | Date;
  /**
   * Options forwarded to Intl.DateTimeFormat (except `timeZone`, which is controlled by the user's chosen timezone)
   */
  formatOptions?: Omit<Intl.DateTimeFormatOptions, "timeZone">;
  /**
   * If true, shows relative dates (Today, Yesterday, or absolute date)
   * If false, always shows absolute date
   * @default false
   */
  showRelative?: boolean;
}

const DEFAULT_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

const DEFAULT_RELATIVE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/**
 * returns an object with the local year/month/day of `date` in the given timezone
 */
const getLocalYMD = (
  date: Date,
  tz: string,
): { year: number; month: number; day: number } => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hourCycle: "h23",
  });

  const parts = formatter.formatToParts(date);
  const map: Record<string, string> = {};
  parts.forEach((p) => {
    map[p.type] = p.value;
  });

  return {
    year: parseInt(map.year, 10),
    month: parseInt(map.month, 10),
    day: parseInt(map.day, 10),
  };
};

const isSameLocalDay = (a: Date, b: Date, tz: string): boolean => {
  const aYMD = getLocalYMD(a, tz);
  const bYMD = getLocalYMD(b, tz);
  return (
    aYMD.year === bYMD.year &&
    aYMD.month === bYMD.month &&
    aYMD.day === bYMD.day
  );
};

const formatTime = (
  date: Date,
  timezone: string | null,
  locale: string,
): string => {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone || "UTC",
  }).format(date);
};

const buildRelativeLabel = (
  date: Date,
  timezone: string | null,
  locale: string,
  todayLabel: string,
  yesterdayLabel: string,
  atLabel: string,
): string | null => {
  const tz = timezone || "UTC";
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const timeStr = formatTime(date, timezone, locale);
  // when no timezone is selected we format in UTC but append "UTC" so the user
  // knows which timezone is being shown.
  const tzSuffix = timezone ? "" : " UTC";

  if (isSameLocalDay(date, now, tz)) {
    return `${todayLabel} ${atLabel} ${timeStr}${tzSuffix}`;
  }

  if (isSameLocalDay(date, yesterday, tz)) {
    return `${yesterdayLabel} ${atLabel} ${timeStr}${tzSuffix}`;
  }

  return null;
};

export const TimezoneDate = ({
  date,
  formatOptions,
  showRelative = false,
}: TimezoneDateProps) => {
  const timezone = useSelector((state: RootState) => state.yourSlice.timezone);
  const i18n = useI18N();
  const language = useLanguage();
  const locale = language ?? "en";

  const todayLabel = i18n("modules.timezoneDate.today");
  const yesterdayLabel = i18n("modules.timezoneDate.yesterday");
  const atLabel = i18n("modules.timezoneDate.at");

  const formatted = useMemo(() => {
    const dateObj = new Date(date);

    // protect against invalid / non-finite dates which cause
    // Intl.DateTimeFormat.format() to throw a RangeError.
    if (!Number.isFinite(dateObj.getTime())) {
      return "";
    }

    if (!showRelative) {
      // Absolute date formatting. When no timezone is selected, add
      // timeZoneName: "short" so the output includes "UTC" explicitly.
      const options: Intl.DateTimeFormatOptions = {
        ...DEFAULT_FORMAT_OPTIONS,
        ...formatOptions,
        timeZone: timezone || "UTC",
        ...(timezone ? {} : { timeZoneName: "short" }),
      };

      return new Intl.DateTimeFormat(locale, options).format(dateObj);
    }

    // relative date formatting (Today / Yesterday / absolute)
    const relativeLabel = buildRelativeLabel(
      dateObj,
      timezone,
      locale,
      todayLabel,
      yesterdayLabel,
      atLabel,
    );
    if (relativeLabel) {
      return relativeLabel;
    }

    // for older dates, use absolute format. Same UTC visibility rule applies.
    const options: Intl.DateTimeFormatOptions = {
      ...DEFAULT_RELATIVE_FORMAT_OPTIONS,
      ...formatOptions,
      timeZone: timezone || "UTC",
      ...(timezone ? {} : { timeZoneName: "short" }),
    };

    return new Intl.DateTimeFormat(locale, options).format(dateObj);
  }, [
    date,
    timezone,
    locale,
    formatOptions,
    showRelative,
    todayLabel,
    yesterdayLabel,
    atLabel,
  ]);

  return <span>{formatted}</span>;
};
